/**
 * Home Page
 *
 * Landing page with create/join room options, discovery map, and recent rooms.
 */

import { Link } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { useP2PContext } from '../contexts'
import { useGeolocation } from '../hooks/useGeolocation'
import { ConnectionStatus } from '../components/common'
import { DiscoveryMap } from '../components/Map'
import { getRecentRooms, type StoredRoom } from '../lib/storage'
import { LocationAccuracyToggle } from '../components/common/LocationAccuracyToggle'
import {
  getStoredAccuracyMode,
  type LocationAccuracyMode,
} from '../components/common/locationAccuracyUtils'

export function HomePage() {
  const { status } = useP2PContext()
  const [recentRooms, setRecentRooms] = useState<StoredRoom[]>([])
  const [accuracyMode, setAccuracyMode] = useState<LocationAccuracyMode>(() =>
    getStoredAccuracyMode()
  )
  const { location, isLoading: locationLoading, requestPermission } = useGeolocation({
    accuracyMode,
    enableIpFallback: true,
  })

  // Re-request location when accuracy mode changes
  const handleAccuracyChange = useCallback(
    (mode: LocationAccuracyMode) => {
      setAccuracyMode(mode)
      setTimeout(() => {
        requestPermission()
      }, 100)
    },
    [requestPermission]
  )

  useEffect(() => {
    // Load recent rooms
    getRecentRooms(5).then(setRecentRooms)
  }, [])

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-3">
      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between p-3 rounded-xl glass-panel relative overflow-hidden mb-3">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
        <h1 className="text-2xl font-display font-bold text-white neon-glow tracking-wider">
          CROWDTALKIE
        </h1>
        <ConnectionStatus status={status} />
      </header>

      {/* Main content - scrollable if needed */}
      <main className="flex-1 min-h-0 flex flex-col overflow-y-auto scrollbar-hide space-y-3">
        {/* Discovery Map Panel */}
        <section className="flex-shrink-0 glass-panel rounded-xl p-1 relative overflow-hidden">
          <DiscoveryMap
            userLocation={location}
            rooms={recentRooms}
            isLoading={locationLoading}
            onRequestLocation={requestPermission}
            headerControls={
              <LocationAccuracyToggle
                value={accuracyMode}
                onChange={handleAccuracyChange}
                compact
              />
            }
          />
          <div className="absolute bottom-0 left-0 w-full p-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
            <span className="text-xs font-mono text-primary/80 px-2 border-l-2 border-primary">
              Secure Connection
            </span>
          </div>
        </section>

        {/* Action buttons */}
        <section className="flex-shrink-0 grid grid-cols-2 gap-2">
          <Link
            to="/create"
            className="group relative overflow-hidden p-4 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/50 transition-all duration-300 neon-border flex flex-col items-center justify-center text-center"
          >
            <div className="absolute inset-0 bg-primary/10 group-hover:bg-primary/20 transition-colors"></div>
            <span className="text-3xl mb-2 relative z-10">📡</span>
            <span className="font-bold text-white relative z-10 tracking-widest uppercase text-sm">Create<br />Room</span>
          </Link>

          <Link
            to="/join"
            className="group relative overflow-hidden p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/20 transition-all duration-300 hover:border-white/40 flex flex-col items-center justify-center text-center"
          >
            <div className="absolute inset-0 bg-white/5 group-hover:bg-white/10 transition-colors"></div>
            <span className="text-3xl mb-2 relative z-10">🔍</span>
            <span className="font-bold text-white relative z-10 tracking-widest uppercase text-sm">Join<br />Room</span>
          </Link>
        </section>

        {/* Hero / Status Text */}
        <section className="flex-shrink-0 text-center py-1">
          <p className="text-xs font-mono text-white/40 tracking-wide">
            Private peer-to-peer communication
          </p>
        </section>

        {/* Features Grid */}
        <section className="flex-shrink-0 grid grid-cols-2 gap-2">
          <FeatureCard
            emoji="🔒"
            title="ENCRYPTED"
            description="Private & secure"
          />
          <FeatureCard
            emoji="⚡"
            title="WORKS OFFLINE"
            description="No internet needed"
          />
        </section>

        {/* Footer Links */}
        <div className="flex justify-center gap-6 border-t border-white/5 pt-4">
          <Link
            to="/docs"
            className="text-xs font-mono text-white/30 hover:text-primary transition-colors"
          >
            Help & Guide
          </Link>
          <Link
            to="/mesh-hardware"
            className="text-xs font-mono text-white/30 hover:text-primary transition-colors"
          >
            Mesh Hardware
          </Link>
        </div>
      </main>
    </div>
  )
}

function FeatureCard({
  emoji,
  title,
  description,
}: {
  emoji: string
  title: string
  description: string
}) {
  return (
    <div className="p-2 rounded-lg bg-black/20 border border-white/5 flex items-center space-x-3">
      <div className="text-xl opacity-80">{emoji}</div>
      <div className="text-left">
        <div className="text-[10px] font-bold text-white/80 tracking-wider uppercase">{title}</div>
        <div className="text-[9px] text-white/40 font-mono">{description}</div>
      </div>
    </div>
  )
}
